import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, BookOpen, Sparkles, HelpCircle, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";
import EntityFooter from "../components/EntityFooter";
import Breadcrumb from "../components/Breadcrumb";
import { getConcept, getRelated, getConceptEnrichment } from "../lib/learnConcepts";
import { getForSlugByIndustryKey, getForIndustry } from "../lib/forIndustries";
import {
  setStandardMeta,
  upsertJsonLd,
  buildArticleSchema,
  buildBreadcrumbSchema,
  buildDefinedTermSchema,
  buildFaqSchema,
  buildOrganizationSchema,
  derivePaaForConcept,
  ORG,
} from "../lib/seo";

const TONE_STYLES = {
  good: "text-[#2C5C43] bg-[#2C5C43]/8 border-[#2C5C43]/20",
  warn: "text-[#9c6b1d] bg-[#9c6b1d]/8 border-[#9c6b1d]/20",
  bad: "text-[#A23E2A] bg-[#A23E2A]/8 border-[#A23E2A]/20",
  neutral: "text-[#5C665D] bg-[#5C665D]/8 border-[#5C665D]/20",
};

export default function LearnConceptPage() {
  const { slug } = useParams();
  const concept = getConcept(slug);
  const enrichment = getConceptEnrichment(slug);

  useEffect(() => {
    if (!concept) return;
    const url = `${window.location.origin}/learn/${concept.slug}`;
    const title = `${concept.title} — Definition, Formula & Benchmarks | CFO Grade`;
    const desc = (enrichment?.answerFirst || concept.short).slice(0, 200);

    setStandardMeta({ title, description: desc, url });

    // Multi-schema graph — Organization + Article + DefinedTerm + FAQPage +
    // BreadcrumbList. Co-locating them in a single @graph helps AI parsers
    // see the relationships between brand, page, hierarchy, and Q&A content.
    const paa = enrichment?.paa || derivePaaForConcept(concept);
    const breadcrumbs = [
      { name: "Home", url: ORG.url },
      { name: "Glossary", url: `${ORG.url}/learn` },
      { name: concept.title, url },
    ];
    upsertJsonLd("learn-graph", {
      "@context": "https://schema.org",
      "@graph": [
        buildOrganizationSchema(),
        buildArticleSchema({ title, description: desc, url }),
        buildDefinedTermSchema({
          name: concept.title,
          description: concept.short,
          slug: concept.slug,
        }),
        buildBreadcrumbSchema(breadcrumbs),
        buildFaqSchema(paa),
      ],
    });
  }, [concept, enrichment]);

  if (!concept) {
    return <Navigate to="/learn" replace />;
  }

  const related = getRelated(slug);
  const analyzeHref = concept.industryKey
    ? `/analyze?industry=${encodeURIComponent(concept.industryKey)}&utm_source=learn&utm_campaign=${encodeURIComponent(concept.slug)}`
    : `/analyze?utm_source=learn&utm_campaign=${encodeURIComponent(concept.slug)}`;
  const paa = enrichment?.paa || derivePaaForConcept(concept);

  // Optional cross-link to the matching /for/{industry} landing page.
  // Only renders when the concept declares an industryKey AND a /for/ page
  // exists for that key. Boosts crawl signal between glossary and industry hubs.
  const forSlug = getForSlugByIndustryKey(concept.industryKey);
  const forIndustry = forSlug ? getForIndustry(forSlug) : null;

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain" data-testid="learn-concept-page">
      <header className="max-w-3xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandWordmark size="sm" />
        </Link>
        <Link
          to="/learn"
          className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> All concepts
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 pt-6 pb-20">
        <Breadcrumb
          items={[
            { name: "Home", to: "/" },
            { name: "Glossary", to: "/learn" },
            { name: concept.title, to: `/learn/${concept.slug}` },
          ]}
        />
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C665D] bg-white border border-[#E5E0D8] rounded-full px-3 py-1.5 mt-4">
          <BookOpen size={13} strokeWidth={1.5} /> {concept.category}
        </div>

        <h1
          className="font-serif-display text-4xl sm:text-5xl text-[#1F382E] leading-[1.05] tracking-tight mt-5"
          data-testid="learn-concept-title"
        >
          {concept.title}
        </h1>

        {/* Answer-first card — the first thing humans AND AI crawlers see. */}
        <div
          className="mt-7 bg-white border-l-2 border-[#C26D5C] border-y border-r border-y-[#E5E0D8] border-r-[#E5E0D8] rounded-r-xl rounded-l-sm p-5 sm:p-6"
          data-testid="learn-answer-first"
        >
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#C26D5C]">
            Short answer
          </p>
          <p className="text-[16px] sm:text-[17px] text-[#1A1A1A] leading-relaxed mt-2">
            {enrichment?.answerFirst || concept.short}
          </p>
        </div>

        {/* Quotable stat — designed to be cited by LLMs */}
        {enrichment?.quickStat && (
          <div
            className="mt-6 flex gap-3 items-start bg-[#1F382E]/5 border border-[#1F382E]/10 rounded-xl p-4 sm:p-5"
            data-testid="learn-quick-stat"
          >
            <Sparkles
              size={16}
              strokeWidth={1.5}
              className="text-[#C26D5C] mt-0.5 shrink-0"
            />
            <p className="text-sm text-[#1F382E] leading-relaxed">
              <span className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D] mr-2">
                Worth quoting
              </span>
              {enrichment.quickStat}
            </p>
          </div>
        )}

        {/* Formula card */}
        <section className="mt-10">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">Formula</p>
          <div className="mt-3 bg-white border border-[#E5E0D8] rounded-xl p-5 sm:p-6">
            <p className="font-mono text-[15px] text-[#1F382E] leading-relaxed">{concept.formula}</p>
            <p className="text-sm text-[#5C665D] mt-3 leading-relaxed">{concept.formulaPlain}</p>
          </div>
        </section>

        {/* Why it matters */}
        <section className="mt-10">
          <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">Why it matters</h2>
          <p className="text-[15px] text-[#1A1A1A] leading-relaxed mt-3">{concept.why}</p>
        </section>

        {/* Benchmarks */}
        <section className="mt-10">
          <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">Benchmarks</h2>
          <div className="mt-4 space-y-2.5">
            {concept.benchmarks.map((b, i) => (
              <div
                key={i}
                className={`flex items-center justify-between px-4 py-3 rounded-lg border ${TONE_STYLES[b.tone] || TONE_STYLES.neutral}`}
                data-testid={`learn-benchmark-${i}`}
              >
                <span className="text-sm font-medium">{b.label}</span>
                <span className="text-sm font-semibold tabular-nums">{b.value}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Worked example */}
        {concept.example && (
          <section className="mt-10">
            <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">Worked example</h2>
            <div className="mt-4 bg-white border border-[#E5E0D8] rounded-xl p-5 sm:p-6">
              <ul className="space-y-2 mb-4">
                {concept.example.inputs.map((row, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="text-[#5C665D]">{row.k}</span>
                    <span className="font-medium text-[#1F382E] tabular-nums">{row.v}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-[#E5E0D8]">
                <p className="font-mono text-[14px] text-[#1F382E] leading-relaxed">{concept.example.result}</p>
              </div>
            </div>
          </section>
        )}

        {/* People-also-ask — AEO gold. Renders as expandable Q&A list. */}
        <section className="mt-12" data-testid="learn-paa">
          <div className="flex items-center gap-2">
            <HelpCircle size={16} strokeWidth={1.5} className="text-[#5C665D]" />
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
              People also ask
            </p>
          </div>
          <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight mt-2">
            Common questions about {concept.title}
          </h2>
          <div className="mt-5 divide-y divide-[#E5E0D8] border-y border-[#E5E0D8]">
            {paa.map((item, i) => (
              <details
                key={i}
                className="group py-4"
                data-testid={`learn-paa-item-${i}`}
                {...(i === 0 ? { open: true } : {})}
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-[15px] font-medium text-[#1F382E] hover:text-[#C26D5C] transition-colors">
                  <span>{item.q}</span>
                  <span className="text-[#5C665D] group-open:rotate-45 transition-transform text-lg leading-none">
                    +
                  </span>
                </summary>
                <p className="text-[14.5px] text-[#1A1A1A] leading-relaxed mt-3 pr-6">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12 bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <p className="font-serif-display text-xl text-[#1F382E] tracking-tight">
              See your business's {concept.title.toLowerCase()}.
            </p>
            <p className="text-sm text-[#5C665D] mt-2 max-w-md">
              Paste your numbers and CFO Grade computes this — plus 23 other ratios — in seconds, with your
              industry's benchmark already loaded.
            </p>
          </div>
          <Link to={analyzeHref}>
            <Button
              className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-6 transition-all duration-300"
              data-testid="learn-concept-cta-analyze"
            >
              Run a free analysis <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
            </Button>
          </Link>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-14">
            <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">Related concepts</h2>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/learn/${r.slug}`}
                  className="group flex items-center justify-between bg-white border border-[#E5E0D8] hover:border-[#1F382E]/30 rounded-xl px-4 py-3 transition-all"
                  data-testid={`learn-related-${r.slug}`}
                >
                  <span className="text-sm font-medium text-[#1F382E] group-hover:text-[#C26D5C] transition-colors">
                    {r.title}
                  </span>
                  <ArrowRight
                    size={14}
                    strokeWidth={1.5}
                    className="text-[#5C665D] group-hover:translate-x-0.5 transition-transform"
                  />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Cross-link to matching industry hub — only when both a key and
            a published /for/{slug} page exist for it. */}
        {forIndustry && forSlug && (
          <section
            className="mt-12 bg-[#1F382E]/4 border border-[#1F382E]/10 rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            data-testid="learn-industry-crosslink"
          >
            <div className="flex items-start gap-3">
              <Building2 size={18} strokeWidth={1.5} className="text-[#C26D5C] mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium">
                  Where this matters most
                </p>
                <p className="font-serif-display text-lg text-[#1F382E] tracking-tight leading-tight mt-1">
                  See {concept.title} in the context of {forIndustry.sub.toLowerCase()}.
                </p>
                <p className="text-sm text-[#5C665D] mt-1.5 max-w-md">
                  Industry-specific benchmarks, common pitfalls, and what lenders look for in this sector.
                </p>
              </div>
            </div>
            <Link
              to={`/for/${forSlug}`}
              className="text-sm font-medium text-[#1F382E] hover:text-[#C26D5C] inline-flex items-center gap-1.5 transition-colors whitespace-nowrap shrink-0"
              data-testid={`learn-industry-crosslink-${forSlug}`}
            >
              {forIndustry.sub} hub <ArrowRight size={13} strokeWidth={1.5} />
            </Link>
          </section>
        )}

        <EntityFooter />
      </main>

      <SiteFootnote />
    </div>
  );
}
